// src/pages/AdminDashboard.jsx
import { useState, useEffect, useMemo, useRef } from "react";
import { useDarkMode, DM } from "../context/DarkModeContext";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import DashboardHeader from "../components/DashboardHeader";
import { useAuth } from "../context/AuthContext";
import { loadAllSections } from "../utils/sectionUtils";
import { SECTIONS, SECTION_FIELDS } from "../constants/sections";
import DentalNewsTicker from "../components/DentalNewsTicker";
import SummaryStrip from "../components/SummaryStrip";
import DepartmentGrid from "../components/DepartmentGrid";
import { Department, COLOR_NAMES } from "../types/department";
import { fetchAllFacultyStats } from "../utils/fetchFacultyStats";

function getProfileDepartment(faculty) {
  const source = faculty?.data || faculty || {};
  return (source.department || source.departmentName || "").toString().trim();
}

export default function AdminDashboard() {
  const [facultyList, setFacultyList] = useState([]);
  const [sectionCounts, setSectionCounts] = useState({}); // Store real-time section counts
  const [loading, setLoading] = useState(true);
  const [countsLoading, setCountsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoverMenu, setHoverMenu] = useState(null);

  // Stats calculation over selected departments/sections
  const [resetMsg, setResetMsg] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedSection, setSelectedSection] = useState(null);
  const [viewFacultySection, setViewFacultySection] = useState(null); // { faculty, section }
  const [openMenuId, setOpenMenuId] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const { logout, currentUser } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const d = darkMode;
  const [allSections, setAllSections] = useState(SECTIONS);
  const navigate = useNavigate();

  useEffect(() => { 
    fetchFaculty(); 
    fetchAdminProfile(); 
    loadAllSections().then(setAllSections);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!hoverMenu) return;
    function handleClickOutside(e) {
      if (e.target.closest('.dropdown-container')) return;
      setHoverMenu(null); 
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [hoverMenu]);

  async function fetchAdminProfile() {
    try {
      const res = await api.get(`/faculty/${currentUser.uid}/profile`);
      if (res) setAdminProfile(res.data || res);
    } catch (err) { console.error("Error fetching admin profile:", err); }
  }

  async function fetchFaculty() {
    try {
      setLoading(true);
      setCountsLoading(true);
      const res = await api.get(`/users?orderBy=createdAt&order=desc`);
      const data = Array.isArray(res) ? res : [];
      const defaultArrays = {
        articlePublications: [], textBookPublications: [], patents: [], copyrights: [],
        fellowships: [], phd: [], swayamCourses: [], awards: [], innovativeTeaching: [],
        eContent: [], researchGrants: [], guestLectures: [], conferencePresentation: [], shortResearchProjects: [],
      };
      // faculty + admin — both show in table, both count in sections
      // superadmin excluded — only manages, no data entry
      const facultyData = data
          .map(row => {
            const profileData = typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row;
            const department = getProfileDepartment(profileData);
            return {
              ...defaultArrays,
              id: row.id,
              data: profileData,
              ...profileData,
              department,
              departmentName: department,
            };
          })
          .filter(u => u.role === "faculty" || u.role === "admin");
      console.log("[AdminDashboard] fetched faculty IDs", facultyData.map((f) => ({ id: f.id, type: typeof f.id })));
      setFacultyList(facultyData);
      
      // Fetch real-time section counts from API
      await fetchSectionCounts(facultyData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); setCountsLoading(false); }
  }

  async function fetchSectionCounts(facultyData) {
    try {
      const stats = await fetchAllFacultyStats(facultyData, api.get);
      const counts = Object.fromEntries(stats.map((item) => [item.id, item]));
      setSectionCounts(counts);
    } catch (err) {
      console.error("Error fetching section counts:", err);
    }
  }

  async function handlePasswordReset(email) {
    try {
      await api.post(`/auth/send-reset-email`, { email });
      setResetMsg("✅ Password reset email sent successfully.");
      setTimeout(() => setResetMsg(""), 5000);
    } catch {
      setResetMsg("❌ Failed to send reset email.");
      setTimeout(() => setResetMsg(""), 5000);
    }
  }

  async function handleMakeAdmin(faculty) {
    if (!window.confirm(`Give ADMIN rights to "${faculty.name}"?

They will have full admin access.`)) return;
    try {
      await api.put(`/users/${faculty.id}`, { role: "admin" });
      setResetMsg(`✅ "${faculty.name}" is now an Admin!`);
      setTimeout(() => setResetMsg(""), 5000);
      fetchFaculty();
    } catch {
      setResetMsg("❌ Failed to update role.");
      setTimeout(() => setResetMsg(""), 5000);
    }
  }

  async function handleRemoveAdmin(faculty) {
    if (!window.confirm(`Remove ADMIN rights from "${faculty.name}"?

They will become a faculty member.`)) return;
    try {
      await api.put(`/users/${faculty.id}`, { role: "faculty" });
      setResetMsg(`✅ "${faculty.name}" is now a Faculty member.`);
      setTimeout(() => setResetMsg(""), 5000);
      fetchFaculty();
    } catch {
      setResetMsg("❌ Failed to update role.");
      setTimeout(() => setResetMsg(""), 5000);
    }
  }

  async function handleToggleStatus(faculty) {
    const isActive = faculty.status !== "inactive";
    const action = isActive ? "Deactivate" : "Activate";
    if (!window.confirm(`${action} "${faculty.name}"?

${isActive ? "They will not be able to login." : "They will be able to login again."}`)) return;
    try {
      await api.put(`/users/${faculty.id}`, {
        status: isActive ? "inactive" : "active",
        statusChangedAt: new Date().toISOString(),
      });
      setResetMsg(`✅ "${faculty.name}" is now ${isActive ? "Inactive" : "Active"}.`);
      setTimeout(() => setResetMsg(""), 5000);
      fetchFaculty();
    } catch {
      setResetMsg("❌ Failed to update status.");
      setTimeout(() => setResetMsg(""), 5000);
    }
  }

  const filtered = facultyList.filter(f => {
    const department = getProfileDepartment(f);
    const matchSearch = f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.empId?.toLowerCase().includes(searchTerm.toLowerCase());
    const isInactive = f.status === "inactive";
    const matchStatus = statusFilter === "all" ? true : statusFilter === "inactive" ? isInactive : !isInactive;
    return matchSearch && matchStatus;
  });

  async function handleLogout() { await logout(); window.location.href = "/admin"; }

  const hasRight = (key) => adminProfile?.role === "superadmin" || (adminProfile?.rights || []).includes(key);
  const shouldShowSettings = adminProfile?.role === "superadmin" || (adminProfile?.rights && adminProfile.rights.length > 0);

  function getSectionTotal(key) {
    // Use real-time API counts if available
    if (Object.keys(sectionCounts).length > 0) {
      return facultyList
        .filter(f => f.status !== "inactive")
        .reduce((sum, f) => sum + (sectionCounts[f.id]?.[key] || 0), 0);
    }
    
    // Fall back to cached data
    return facultyList
      .filter(f => f.status !== "inactive")
      .reduce((sum, f) => sum + (Array.isArray(f[key]) ? f[key].length : (typeof f[key] === "number" ? f[key] : 0)), 0);
  }


  function getFacultyCount(faculty, key) {
    // Use real-time API counts if available, otherwise fall back to cached data
    if (sectionCounts[faculty.id] && sectionCounts[faculty.id][key] !== undefined) {
      return sectionCounts[faculty.id][key];
    }
    
    const val = faculty[key];
    if (Array.isArray(val)) return val.length;
    if (typeof val === "number") return val;
    return 0;
  }

  return (
    <div style={{ minHeight: "100vh", background: DM.pageBg(d), fontFamily: "'Nunito', sans-serif", transition: "background 0.3s" }}>
      <DashboardHeader 
        label={adminProfile?.role === "superadmin" ? "⭐ SUPER ADMIN" : "👑 ADMIN"}
        showNewsTicker={true}
      >
        {adminProfile?.name && (
          <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 15, fontWeight: 700, marginRight: 8 }}>
            {adminProfile.name}
          </span>
        )}
        
        <button onClick={() => navigate("/my-portfolio")} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🎓 My Portfolio</button>

          {/* Reports Dropdown - click based */}
          <div style={{ position: "relative" }} className="dropdown-container">
            <button
              onClick={() => setHoverMenu(hoverMenu === "reports" ? null : "reports")}
              style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer", display: "flex", gap: 6, alignItems: "center" }}
            >
              📊 Reports <span style={{ fontSize: 13 }}>▼</span>
            </button>
            {hoverMenu === "reports" && (
              <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "#fff", borderRadius: 12, boxShadow: "0 10px 25px rgba(0,0,0,0.15)", overflow: "hidden", minWidth: 220, zIndex: 1000, display: "flex", flexDirection: "column", padding: "8px 0" }}>
                <button onClick={() => { setHoverMenu(null); navigate("/my-report"); }} style={{ background: "transparent", border: "none", padding: "12px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>📋 My Report</button>
                {hasRight("all_faculty_report") && (
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/report"); }} style={{ background: "transparent", border: "none", padding: "12px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>📊 All Faculty Reports</button>
                )}
                {hasRight("metrics_report") && (
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/metrics-report", { state: { preloadedUsers: facultyList } }); }} style={{ background: "transparent", border: "none", padding: "12px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>📈 Metrics Report</button>
                )}
                {hasRight("user_management") && (
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/user-logs"); }} style={{ background: "transparent", border: "none", padding: "12px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>🕒 Users Log Report</button>
                )}
                <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0" }} />
                {(hasRight("staff_details_report") || hasRight("faculty_list") || hasRight("staff_affidavit_dci")) && (
                  <div style={{ padding: "4px 20px 2px", fontSize: 13, fontWeight: 900, color: "#94a3b8", letterSpacing: 1 }}>STAFF REPORTS</div>
                )}
                {hasRight("staff_details_report") && (
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/staff-details?type=dci"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#eaf1ff"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>📋</span> DCI Report</button>
                )}
                {hasRight("staff_details_report") && (
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/staff-details?type=tnmgr"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f0fdf4"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>🎓</span> TNMGR University Report</button>
                )}
                {hasRight("faculty_list") && (
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/staff-details?type=faculty_list"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#fff7ed"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>👥</span> Faculty List</button>
                )}
                {hasRight("staff_affidavit_dci") && (
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/affidavit-dci"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 16, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#fffbeb"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>📄</span> Staff Affidavit DCI</button>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => navigate("/admin/users", { state: { preloadedUsers: facultyList } })}
            style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}
          >
            👥 Faculty Members
          </button>

          {/* Settings Dropdown - click based */}
          {shouldShowSettings && (
            <div style={{ position: "relative" }} className="dropdown-container">
              <button
                onClick={() => setHoverMenu(hoverMenu === "settings" ? null : "settings")}
                style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer", display: "flex", gap: 6, alignItems: "center" }}
              >
                ⚙️ Settings <span style={{ fontSize: 13 }}>▼</span>
              </button>
              {hoverMenu === "settings" && (
                <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "#fff", borderRadius: 12, boxShadow: "0 10px 25px rgba(0,0,0,0.15)", overflow: "hidden", minWidth: 200, zIndex: 1000, display: "flex", flexDirection: "column", padding: "8px 0" }}>
                  <div style={{ padding: "8px 20px 4px", fontSize: 13, fontWeight: 900, color: "#94a3b8", letterSpacing: 1 }}>USER</div>
                  {hasRight("create_faculty") && (
                    <button onClick={() => { setHoverMenu(null); navigate("/admin/create-faculty"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 15, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>➕</span> Create Faculty</button>
                  )}
                  {hasRight("user_management") && (
                    <button onClick={() => { setHoverMenu(null); navigate("/admin/users", { state: { preloadedUsers: facultyList } }); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 15, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>👥</span> User Management</button>
                  )}
                  {hasRight("designation") && (
                    <button onClick={() => { setHoverMenu(null); navigate("/admin/settings?tab=designations"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 15, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>🎖️</span> Designation</button>
                  )}
                  <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0" }}></div>
                  <div style={{ padding: "8px 20px 4px", fontSize: 13, fontWeight: 900, color: "#94a3b8", letterSpacing: 1 }}>MASTER</div>
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/settings?tab=departments"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 15, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>🏢</span> Department</button>
                  <button onClick={() => { setHoverMenu(null); navigate("/admin/settings?tab=colleges"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 15, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>🎓</span> College</button>
                  {hasRight("section_fields") && (
                    <button onClick={() => { setHoverMenu(null); navigate("/admin/settings?tab=fields"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 15, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>📋</span> Section Field</button>
                  )}
                  {hasRight("sections") && (
                    <button onClick={() => { setHoverMenu(null); navigate("/admin/settings?tab=sections"); }} style={{ background: "transparent", border: "none", padding: "10px 20px", textAlign: "left", fontSize: 15, fontWeight: 600, color: "#1e293b", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><span>⚙️</span> Manage Section</button>
                  )}
                </div>
              )}
            </div>
          )}

          <button onClick={toggleDarkMode} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>
            {darkMode ? "☀️ Light" : "🌙 Dark"}
          </button>
          <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🚪 Logout</button>
      </DashboardHeader>

      {resetMsg && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 999, background: resetMsg.startsWith("✅") ? "#f0fdf4" : "#fef2f2", border: `1px solid ${resetMsg.startsWith("✅") ? "#86efac" : "#fecaca"}`, color: resetMsg.startsWith("✅") ? "#16a34a" : "#dc2626", borderRadius: 12, padding: "14px 20px", fontWeight: 700, fontSize: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", maxWidth: 420 }}>
          {resetMsg}
        </div>
      )}

      <div style={{ padding: "24px 32px" }}>

        {/* Useful Links */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "16px 24px", marginBottom: 20, boxShadow: "0 2px 12px rgba(15,37,87,0.06)", border: "1px solid #e8f0fe" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#64748b", letterSpacing: 1.2, marginBottom: 12 }}>🔗 USEFUL LINKS</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a href="https://swayam.gov.in/" target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff7ed", border: "1.5px solid #fed7aa", borderRadius: 12, padding: "8px 16px", textDecoration: "none" }}>
              <img src="https://storage.googleapis.com/swayam2_central/assets/img/swayam_logo.png"
                style={{ height: 28, objectFit: "contain" }} alt="Swayam" />
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#ea580c" }}>Swayam Portal</div>
                <div style={{ fontSize: 13, color: "#94a3b8" }}>swayam.gov.in</div>
              </div>
            </a>
            <a href="https://scholar.google.com/" target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 12, padding: "8px 16px", textDecoration: "none" }}>
              <img src="https://scholar.google.com/favicon.ico"
                style={{ height: 24, width: 24, objectFit: "contain" }} alt="Google Scholar" />
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#16a34a" }}>Google Scholar</div>
                <div style={{ fontSize: 13, color: "#94a3b8" }}>scholar.google.com</div>
              </div>
            </a>
            <a href="https://vidwan.inflibnet.ac.in/" target="_blank" rel="noreferrer"
              style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0f9ff", border: "1.5px solid #7dd3fc", borderRadius: 12, padding: "8px 16px", textDecoration: "none" }}>
              <img src="https://vidwan.inflibnet.ac.in/images/vidwan-logo.png"
                onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                style={{ height: 28, objectFit: "contain" }} alt="Vidwan" />
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#0284c7" }}>Vidwan</div>
                <div style={{ fontSize: 13, color: "#94a3b8" }}>vidwan.inflibnet.ac.in</div>
              </div>
            </a>
          </div>
        </div>

        {/* Total Faculty + 14 Section Tiles */}
        {loading || countsLoading ? (
          <div style={{ marginBottom: 24 }}>
            <div style={{ background: "linear-gradient(135deg, #0f2557, #2563eb)", borderRadius: 16, padding: "20px 28px", color: "#fff", marginBottom: 14, display: "flex", alignItems: "center", gap: 20, boxShadow: "0 4px 20px rgba(37,99,235,0.2)" }}>
              <div style={{ width: 52, height: 52, background: "rgba(255,255,255,0.15)", borderRadius: 14 }} />
              <div style={{ flex: 1 }}>
                <div style={{ height: 24, width: 140, borderRadius: 999, background: "rgba(255,255,255,0.2)", marginBottom: 8 }} />
                <div style={{ height: 16, width: 220, borderRadius: 999, background: "rgba(255,255,255,0.16)" }} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 12 }}>
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} style={{ background: "#f8fafc", borderRadius: 14, padding: "16px 12px", minHeight: 120 }}>
                  <div style={{ height: 24, width: 24, borderRadius: 999, background: "#e2e8f0", margin: "0 auto 10px" }} />
                  <div style={{ height: 24, width: "70%", background: "#e2e8f0", borderRadius: 999, margin: "0 auto 8px" }} />
                  <div style={{ height: 12, width: "60%", background: "#f1f5f9", borderRadius: 999, margin: "0 auto" }} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 24 }}>
            {/* Total Faculty tile - full width */}
            <div style={{ background: "linear-gradient(135deg, #0f2557, #2563eb)", borderRadius: 16, padding: "20px 28px", color: "#fff", marginBottom: 14, display: "flex", alignItems: "center", gap: 20, boxShadow: "0 4px 20px rgba(37,99,235,0.2)" }}>
              <div style={{ width: 52, height: 52, background: "rgba(255,255,255,0.15)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>👥</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 40, fontWeight: 900 }}>{facultyList.filter(u => u.role === "faculty" && u.status !== "inactive").length}</div>
                <div style={{ fontSize: 16, opacity: 0.8, fontWeight: 600 }}>Total Active Faculty Members</div>
              </div>

            </div>

            {/* 14 Section Tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 12 }}>
              {allSections.map((sec, i) => (
                <div key={i} onClick={() => setSelectedSection(selectedSection?.key === sec.key ? null : sec)}
                  style={{ background: selectedSection?.key === sec.key ? sec.light : DM.cardBg(d), borderRadius: 14, padding: "16px 12px", boxShadow: selectedSection?.key === sec.key ? `0 4px 16px ${sec.color}40` : DM.shadow(d), borderTop: `4px solid ${sec.color}`, border: selectedSection?.key === sec.key ? `2px solid ${sec.color}` : "2px solid transparent", textAlign: "center", cursor: "pointer", transition: "all 0.18s", transform: selectedSection?.key === sec.key ? "translateY(-3px)" : "none" }}>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>{sec.icon}</div>
                  <div style={{ fontSize: 30, fontWeight: 900, color: sec.color, lineHeight: 1 }}>{getSectionTotal(sec.key)}</div>
                  <div style={{ fontSize: 13, color: selectedSection?.key === sec.key ? sec.color : "#475569", fontWeight: selectedSection?.key === sec.key ? 700 : 600, marginTop: 5, lineHeight: 1.4 }}>{sec.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section Report - shows when tile is clicked */}
        {selectedSection && (
          <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden", marginBottom: 24 }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", background: selectedSection.light }}>
              <h2 style={{ fontWeight: 800, fontSize: 19, color: "#0f2557", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 24 }}>{selectedSection.icon}</span>
                {selectedSection.label} — All Faculty Report
                <span style={{ background: selectedSection.color, color: "#fff", borderRadius: 20, padding: "2px 12px", fontSize: 15, fontWeight: 700 }}>{getSectionTotal(selectedSection.key)} Total</span>
              </h2>
              <button onClick={() => setSelectedSection(null)}
                style={{ background: "#fff", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                ✕ Close
              </button>
            </div>
            <div style={{ padding: "16px 24px" }}>
              {facultyList.filter(f => f.status !== "inactive").every(f => !f[selectedSection.key]?.length) ? (
                <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8" }}>
                  <div style={{ fontSize: 38, marginBottom: 10 }}>{selectedSection.icon}</div>
                  <p>No {selectedSection.label} entries from any active faculty yet.</p>
                </div>
              ) : (
                facultyList.filter(f => f.status !== "inactive" && f[selectedSection.key]?.length > 0).map((faculty, fi) => (

                  <div key={fi} style={{ marginBottom: 20 }}>
                    {/* Faculty name header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: `hsl(${fi * 47}, 65%, 55%)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 15 }}>
                        {faculty.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 16 }}>{getNameWithTitle(faculty)}</div>
                        <div style={{ fontSize: 13, color: "#94a3b8" }}>{faculty.department} · {faculty.empId}</div>
                      </div>
                      <span style={{ background: selectedSection.light, color: selectedSection.color, borderRadius: 20, padding: "2px 10px", fontSize: 14, fontWeight: 700, marginLeft: "auto" }}>
                        {faculty[selectedSection.key]?.length} entries
                      </span>
                      <button onClick={() => navigate(`/admin/faculty/${faculty.id}`)}
                        style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "5px 12px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                        View Profile →
                      </button>
                    </div>
                    {/* Entries */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 8 }}>
                      {faculty[selectedSection.key].map((item, ii) => (
                        <SectionEntryRow key={ii} item={item} index={ii + 1} sectionKey={selectedSection.key} sectionData={selectedSection} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* DEPARTMENT-WISE FACULTY OVERVIEW */}
        {facultyList.length > 0 && (() => {
          // Group faculty by department
          const deptMap = new Map();
          facultyList.filter(f => f.status !== "inactive" && f.id !== undefined && f.id !== null).forEach(faculty => {
            const dept = getProfileDepartment(faculty);
            if (!dept) return;
            const normalizedDept = dept.trim().toLowerCase();
            if (!deptMap.has(normalizedDept)) {
              deptMap.set(normalizedDept, []);
            }
            deptMap.get(normalizedDept).push(faculty);
          });

          // Create Department objects
          const departments = Array.from(deptMap.entries()).map(([deptName, members], idx) => {
            const stats = {
              articlePublications: 0,
              textBookPublications: 0,
              patents: 0,
              copyRights: 0,
              fellowship: 0,
              phd: 0,
              swayamCourses: 0,
              awards: 0,
              innovativeTeaching: 0,
              eContent: 0,
              researchGrants: 0,
              guestLectures: 0,
              conferencePresentations: 0,
              shortResearchProjects: 0,
            };

            let totalEntries = 0;
            members.forEach(faculty => {
              stats.articlePublications += getFacultyCount(faculty, "articlePublications");
              stats.textBookPublications += getFacultyCount(faculty, "textBookPublications");
              stats.patents += getFacultyCount(faculty, "patents");
              stats.copyRights += getFacultyCount(faculty, "copyrights");
              stats.fellowship += getFacultyCount(faculty, "fellowships");
              stats.phd += getFacultyCount(faculty, "phd");
              stats.swayamCourses += getFacultyCount(faculty, "swayamCourses");
              stats.awards += getFacultyCount(faculty, "awards");
              stats.innovativeTeaching += getFacultyCount(faculty, "innovativeTeaching");
              stats.eContent += getFacultyCount(faculty, "eContent");
              stats.researchGrants += getFacultyCount(faculty, "researchGrants");
              stats.guestLectures += getFacultyCount(faculty, "guestLectures");
              stats.conferencePresentations += getFacultyCount(faculty, "conferencePresentation");
              stats.shortResearchProjects += getFacultyCount(faculty, "shortResearchProjects");

              allSections.forEach(sec => {
                totalEntries += getFacultyCount(faculty, sec.key);
              });
            });

            const colorName = COLOR_NAMES[idx % COLOR_NAMES.length];
            const displayName = members[0]?.data?.department || members[0]?.data?.departmentName || members[0]?.departmentName || members[0]?.department || deptName;
            return {
              id: (displayName || "department").replace(/\s+/g, "-").toLowerCase(),
              name: displayName || "Unnamed Department",
              facultyCount: members.length,
              totalEntries,
              stats,
              members: members.map(f => ({
                id: f.id,
                name: f.data?.name || f.data?.displayName || f.name || f.displayName || "Unnamed Faculty",
                empId: f.empId,
                email: f.email,
                totalEntries: allSections.reduce((sum, sec) => sum + getFacultyCount(f, sec.key), 0),
                department: getProfileDepartment(f),
                designation: f.designation,
              })),
              accentColor: colorName,
            };
          });

          // Compute summary metrics
          const summaryMetrics = [
            { icon: "🏢", label: "Total Departments", value: departments.length, color: "#2563eb" },
            { icon: "👥", label: "Total Active Faculty", value: facultyList.filter(f => f.status !== "inactive").length, color: "#7c3aed" },
            { icon: "📄", label: "Article Publications", value: getSectionTotal("articlePublications"), color: "#16a34a" },
            { icon: "📚", label: "Text Book Publications", value: getSectionTotal("textBookPublications"), color: "#ea580c" },
            { icon: "🔬", label: "Total Patents", value: getSectionTotal("patents"), color: "#db2777" },
            { icon: "©️", label: "Copy Rights", value: getSectionTotal("copyrights"), color: "#7c3aed" },
          ];

          if (departments.length === 0) return null;

          return (
            <div style={{ marginBottom: 24 }}>
              {/* Summary Strip */}
              <SummaryStrip metrics={summaryMetrics} darkMode={d} />

              {/* Department Grid */}
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 18, fontWeight: 900, color: d ? "#e2e8f0" : "#0f2557", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 22 }}>🏛️</span>
                  Department-wise Faculty Overview
                </h2>
                <DepartmentGrid
                  departments={departments}
                  onViewDepartment={(dept) => navigate("/admin/department", { state: { department: dept } })}
                  darkMode={d}
                />
              </div>
            </div>
          );
        })()}

        {/* Faculty members table removed from the dashboard; use the dedicated Faculty Members page. */}
      </div>

      {viewFacultySection && (
        <ViewEntriesModal 
          faculty={viewFacultySection.faculty} 
          section={viewFacultySection.section} 
          onClose={() => setViewFacultySection(null)} 
        />
      )}

      {/* Footer */}
      <footer style={{ background: d ? "rgba(255,255,255,0.03)" : "#f8fafc", borderTop: `1px solid ${d ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`, padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/apdch_logo.png" alt="APDCH" style={{ height: 32, width: 32, objectFit: "contain", opacity: 0.8 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: d ? "#e2e8f0" : "#0f2557" }}>Adhiparasakthi Dental College &amp; Hospital</div>
            <div style={{ fontSize: 13, color: "#94a3b8" }}>Melmaruvathur, Tamil Nadu</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: d ? "rgba(37,99,235,0.15)" : "#eaf1ff", border: `1px solid ${d ? "rgba(37,99,235,0.3)" : "#bfdbfe"}`, borderRadius: 20, padding: "5px 14px" }}>
            <span style={{ fontSize: 16 }}>💻</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: d ? "#93c5fd" : "#1d4ed8" }}>Maintained by</span>
            <span style={{ fontSize: 13, fontWeight: 900, color: d ? "#60a5fa" : "#2563eb" }}>APDCH IT Team</span>
          </div>
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8", textAlign: "right" }}>
          <div>© {new Date().getFullYear()} APDCH · AcadTrack v1.0</div>
          <div style={{ marginTop: 2 }}>All rights reserved</div>
        </div>
      </footer>

    </div>
  );
}

const getNameWithTitle = (faculty) => {
  if (!faculty) return "";
  const title = faculty.title || faculty.salutation || faculty.prefix || "";
  if (!title) return faculty.name || "—";
  return `${title} ${faculty.name}`.trim();
};

function ViewEntriesModal({ faculty, section, onClose }) {
  const entries = faculty[section.key] || [];
  
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      {/* Modal Container */}
      <div 
        style={{ background: "#fff", borderRadius: 24, width: "100%", maxWidth: 800, maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #e2e8f0", overflow: "hidden" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "20px 28px", background: section.light, borderBottom: `1px solid ${section.color}20`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: section.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>
              {section.icon}
            </div>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 900, color: "#0f2557", margin: 0 }}>{section.label} Details</h2>
              <div style={{ fontSize: 14, color: "#64748b", fontWeight: 600, marginTop: 2 }}>
                {getNameWithTitle(faculty)} · {faculty.empId} · {entries.length} Entries
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.05)", color: "#64748b", cursor: "pointer", fontSize: 20, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.1)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(0,0,0,0.05)"}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "24px", overflowY: "auto", background: "#f8faff" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {entries.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>{section.icon}</div>
                <p>No entries found for this category.</p>
              </div>
            ) : (
              entries.map((item, ii) => (
                <SectionEntryRow key={ii} item={item} index={ii + 1} sectionKey={section.key} sectionData={section} />
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9", textAlign: "right", background: "#fff" }}>
          <button 
            onClick={onClose}
            style={{ background: "linear-gradient(135deg, #0f2557, #2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 800, fontSize: 16, cursor: "pointer", boxShadow: "0 4px 12px rgba(37,99,235,0.3)" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const thStyle = { padding: "12px 14px", textAlign: "left", fontSize: 13, fontWeight: 700, color: "#64748b", letterSpacing: 0.5, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];



const fieldsCache = {};

async function getCachedFields(sectionKey) {
  if (fieldsCache[sectionKey]) return fieldsCache[sectionKey];
  try {
    const res = await api.get(`/section_fields_${sectionKey}?orderBy=order`);
    const fields = Array.isArray(res) && res.length ? res : (SECTION_FIELDS[sectionKey] || []);
    fieldsCache[sectionKey] = fields;
    return fields;
  } catch {
    return SECTION_FIELDS[sectionKey] || [];
  }
}

function SectionEntryRow({ item, index, sectionKey, sectionData }) {
  const [fields, setFields] = useState([]);

  useEffect(() => {
    let active = true;
    getCachedFields(sectionKey).then(res => {
      if (active) setFields(res);
    });
    return () => { active = false; };
  }, [sectionKey]);

  function getKey(f) { return f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_"); }

  const titleField = fields.find(f => f.required);
  const title = titleField ? item[getKey(titleField)] : Object.values(item).find(v => v && typeof v === "string" && !["addedAt","updatedAt"].includes(v));

  return (
    <div style={{ background: sectionData?.light, borderRadius: 10, padding: "12px 16px", borderLeft: `3px solid ${sectionData?.color}`, display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div style={{ width: 22, height: 22, borderRadius: "50%", background: sectionData?.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{index}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 15 }}>{title || "—"}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 16px", marginTop: 4 }}>
          {fields.filter(f => !f.required).map(f => {
            const val = item[getKey(f)];
            const isLink = getKey(f).toLowerCase().includes("link") && typeof val === "string" && val.startsWith("http");
            const isNptel = isLink && (val.includes("swayam") || val.includes("nptel"));
            return val ? (
              <span key={getKey(f)} style={{ fontSize: 13, color: "#64748b" }}>
                <span style={{ fontWeight: 600, color: "#475569" }}>{f.label || f.name}:</span>{" "}
                {isLink ? (
                  <a href={val} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, background: isNptel ? "#fff7ed" : "#eaf1ff", border: `1px solid ${isNptel ? "#fed7aa" : "#bfdbfe"}`, borderRadius: 6, padding: "2px 8px", color: isNptel ? "#ea580c" : "#2563eb", fontWeight: 700, textDecoration: "none", fontSize: 13 }}>
                    {isNptel ? (
                      <img src="https://storage.googleapis.com/swayam2_central/assets/img/swayam_images/NC_logo/NPTEL_logo_128.png"
                        style={{ height: 13, objectFit: "contain" }} alt="NPTEL" />
                    ) : "🔗"} Open
                  </a>
                ) : val}
              </span>
            ) : null;
          })}
        </div>
      </div>
      {item.currentStatus && (
        <span style={{ background: item.currentStatus === "Published" || item.currentStatus === "Completed" || item.currentStatus === "Received" ? "#dcfce7" : "#fef3c7", color: item.currentStatus === "Published" || item.currentStatus === "Completed" || item.currentStatus === "Received" ? "#16a34a" : "#d97706", borderRadius: 20, padding: "2px 10px", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
          {item.currentStatus}
        </span>
      )}
    </div>
  );
}


